import Swiper from 'swiper';
import { EffectFade, Navigation, Thumbs } from 'swiper/modules';
import videojs from 'video.js';
import 'videojs-youtube';

export class Lightbox {
    constructor(data) {
        if (!data) return;
        this.data = data;
        this.init();
    }

    async init() {
        this.root = null;

        this.sliderElement = null;
        this.sliderInstance = null;

        this.sliderThumbnailsElement = null;
        this.sliderThumbnailsInstance = null;

        this.closeButton = null;
        this.activeClass = '-active';

        this.players = [];

        this.createLightbox();
        this.createSwipers();

        this.events();
    }

    createSwipers() {
        if (this.sliderThumbnailsElement) {
            this.sliderThumbnailsInstance = new Swiper(
                this.sliderThumbnailsElement,
                {
                    spaceBetween: 16,
                    breakpoints: {
                        0: {
                            slidesPerView: 5.22,
                        },
                        1024: {
                            slidesPerView: 'auto',
                        },
                    },
                }
            );
        }

        if (this.sliderElement) {
            this.sliderInstance = new Swiper(this.sliderElement, {
                slidesPerView: 1,
                modules: [EffectFade, Thumbs, Navigation],
                effect: 'fade',
                fadeEffect: {
                    crossFade: true,
                },
                thumbs: {
                    swiper: this.sliderThumbnailsInstance,
                },
                navigation: {
                    prevEl: this.prevArrow,
                    nextEl: this.nextArrow,
                },
            });

            this.sliderInstance.on('slideChange', () => {
                this.players.forEach(element => {
                    element.videoInstance.pause();
                });
            });
        }
    }

    events() {
        if (this.closeButton) {
            this.closeButton.addEventListener(
                'click',
                this.closeLightbox.bind(this)
            );
        }
    }

    closeLightbox() {
        this.root.classList.remove(this.activeClass);

        if (this.players.length) {
            this.players.forEach(element => {
                element.videoInstance.pause();
            });
        }
    }

    openLightbox(slide = 0) {
        this.sliderInstance.slideTo(slide);
        const { slides } = this.sliderInstance;

        const currentSlide = slides[slide];

        if (currentSlide) {
            const player = this.players.find(item => item.index == slide);
            if (player) {
                currentSlide.classList.add('playing');
                player.videoInstance.play();
            }
        }

        setTimeout(() => {
            this.root.classList.add(this.activeClass);
        }, 100);
    }

    createLightbox() {
        const lightboxElement = document.createElement('div');
        lightboxElement.classList.add(
            'z-10000',
            'min-h-[500px]',
            'bg-white',
            'fixed',
            'top-0',
            'left-0',
            'w-full',
            'h-full',
            'pointer-events-none',
            'invisible',
            'transition-all',
            'opacity-0',
            'flex',
            'flex-col',
            `[&.-active]:opacity-100`,
            `[&.-active]:visible`,
            `[&.-active]:pointer-events-auto`
        );
        const top = this.createTop();
        lightboxElement.appendChild(top);

        const content = this.createContent();
        lightboxElement.appendChild(content);

        const bottom = this.createBottom();
        lightboxElement.appendChild(bottom);

        this.root = lightboxElement;
        document.body.appendChild(this.root);
    }

    createTop() {
        const top = document.createElement('div');
        top.classList.add(
            'flex',
            'items-center',
            'justify-end',
            'p-12',
            'border-b',
            'border-gray-60'
        );
        const close = document.createElement('button');
        close.setAttribute('type', 'button');
        close.classList.add(
            'icon',
            'icon-close',
            'cursor-pointer',
            'text-[20px]!',
            'p-[14px]',
            'flex',
            'items-center',
            'justify-center',
            'max-desktop:rounded-full',
            'max-desktop:bg-gray-10'
        );
        top.appendChild(close);

        this.closeButton = close;

        return top;
    }

    createContent() {
        const content = document.createElement('div');
        content.classList.add('relative', 'flex-1', 'min-h-0');

        const container = document.createElement('div');
        container.classList.add('container', 'h-full');

        const row = document.createElement('div');
        row.classList.add('row', 'h-full', 'relative');

        const col = document.createElement('div');
        col.classList.add(
            'col-span-24',
            'desktop:col-span-20',
            'desktop:col-start-3',
            'min-h-0'
        );

        const swiperEl = document.createElement('div');
        swiperEl.classList.add('h-full', 'py-48');
        const swiperWrapper = document.createElement('div');
        swiperWrapper.classList.add('h-full');
        swiperWrapper.classList.add('swiper-wrapper');

        this.data.forEach((element, key) => {
            const slide = document.createElement('div');
            slide.classList.add('swiper-slide', 'h-full');

            const { image, thumbnail, is_movie, youtube_id, movie } = element;
            const wrapper = document.createElement('div');
            wrapper.classList.add('h-full', 'relative', 'wrapper');

            const img = document.createElement('img');
            img.src = image?.high ?? thumbnail.high;
            img.classList.add(
                'w-full',
                'h-full',
                'object-contain',
                'rounded-small'
            );

            let video = null;

            if (is_movie && youtube_id && !movie) {
                video = document.createElement('video');
                video.classList.add(
                    'video-js',
                    'vjs-default-skin',
                    'vjs-fluid',
                    'w-full',
                    'h-full',
                    'object-contain'
                );
                video.setAttribute('controls', '1');
                video.setAttribute('preload', 'auto');
                video.setAttribute(
                    'data-setup',
                    JSON.stringify({
                        techOrder: ['youtube'],
                        sources: [
                            {
                                type: 'video/youtube',
                                src: `https://www.youtube.com/watch?v=${youtube_id}`,
                            },
                        ],
                    })
                );
            } else if (is_movie && movie) {
                video = document.createElement('video');
                video.classList.add(
                    'video-js',
                    'vjs-default-skin',
                    'vjs-fluid',
                    'w-full',
                    'h-full',
                    'object-contain'
                );
                video.setAttribute('controls', '1');
                video.setAttribute('preload', 'auto');
                const source = document.createElement('source');
                source.src = movie.url;
                source.setAttribute('type', 'video/mp4');

                video.appendChild(source);
            }

            if (video) {
                slide.classList.add(
                    '[&.playing_.video-js]:block!',
                    '[&_.video-js]:hidden!',
                    '[&.playing_.wrapper]:hidden!',
                    '[&_.wrapper]:block!',
                    '[&_.video-js]:w-full!',
                    '[&_.video-js]:h-full!'
                );
                slide.appendChild(video);
                const videoInstance = videojs(video, {
                    controls: false,
                    preload: 'auto',
                    fluid: false,
                });

                this.players.push({
                    index: key,
                    videoInstance,
                });

                const icon = document.createElement('button');
                icon.setAttribute('type', 'button');
                icon.classList.add('icon', 'icon-play', 'play');
                icon.addEventListener('click', e =>
                    this.onPlayButtonClick(e, videoInstance)
                );

                videoInstance.on('pause', e => {
                    slide.classList.remove('playing');
                });
                videoInstance.on('ended', () => {
                    slide.classList.remove('playing');
                });
                videoInstance.el_.addEventListener('click', () => {
                    slide.classList.remove('playing');
                });
                videoInstance.el_.addEventListener('touchend', () => {
                    slide.classList.remove('playing');
                });

                wrapper.appendChild(icon);
            }

            wrapper.appendChild(img);
            slide.appendChild(wrapper);
            swiperWrapper.appendChild(slide);
        });

        swiperEl.appendChild(swiperWrapper);
        this.sliderElement = swiperEl;
        col.appendChild(swiperEl);

        const arrowsEl = document.createElement('div');
        arrowsEl.classList.add(
            'top-[50%]',
            'absolute',
            '-translate-y-[50%]',
            'flex',
            'items-center',
            'justify-between',
            'w-full',
            'z-10',
            'pointer-events-none'
        );

        this.prevArrow = this.createArrow();
        this.nextArrow = this.createArrow('next');
        arrowsEl.appendChild(this.prevArrow);
        arrowsEl.appendChild(this.nextArrow);

        row.appendChild(col);
        row.appendChild(arrowsEl);
        container.appendChild(row);
        content.appendChild(container);
        return content;
    }

    createArrow(type = 'prev') {
        const button = document.createElement('button');
        button.setAttribute('type', 'button');
        button.setAttribute('data-swiper', type);
        button.classList.add(
            'z-2',
            'button',
            'button-swiper-nav',
            '-blur',
            'pointer-events-auto'
        );

        const icon = document.createElement('span');
        icon.classList.add(
            'icon',
            'flex',
            type === 'prev' ? 'icon-arrow-left' : 'icon-arrow-right'
        );
        button.appendChild(icon);
        return button;
    }

    onPlayButtonClick(e, videoInstance) {
        const slide = e.target.closest('.swiper-slide');
        slide.classList.add('playing');
        videoInstance.play();
    }

    createBottom() {
        const bottom = document.createElement('div');
        bottom.classList.add('p-12', 'border-t', 'border-gray-60');

        const container = document.createElement('div');
        container.classList.add('container', 'max-desktop:px-0!');

        const swiperEl = document.createElement('div');
        const swiperWrapper = document.createElement('div');
        swiperWrapper.classList.add('swiper-wrapper', 'desktop:justify-center');

        this.data.forEach(element => {
            const slide = document.createElement('div');
            slide.classList.add(
                'swiper-slide',
                'desktop:flex-1',
                'desktop:max-w-[75px]',
                'aspect-square',
                'overflow-hidden',
                'rounded-small',
                'border-2',
                'border-transparent',
                'transition-all',
                '[&.swiper-slide-thumb-active]:border-primary'
            );

            const { image, thumbnail } = element;

            const img = document.createElement('img');
            img.src = image?.low ?? thumbnail.low;
            img.classList.add('w-full', 'h-full', 'object-cover');

            slide.appendChild(img);
            swiperWrapper.appendChild(slide);
        });

        this.sliderThumbnailsElement = swiperEl;

        swiperEl.appendChild(swiperWrapper);
        container.appendChild(swiperEl);
        bottom.appendChild(container);
        return bottom;
    }
}
